import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { MessageCircle, Loader2 } from "lucide-react";
import { HOTLINE_DIGITS } from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingForm({ rooms = [], preselectRoomId = null, compact = false }) {
  const { t, lang } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    name: "", phone: "", check_in: today, check_out: tomorrow,
    guests: 2, room_id: preselectRoomId || "", message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preselectRoomId) setForm((p) => ({ ...p, room_id: preselectRoomId }));
  }, [preselectRoomId]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.check_in || !form.check_out) {
      toast.error(lang === 'en' ? "Please fill all required fields" : "অনুগ্রহ করে প্রয়োজনীয় ফিল্ড পূরণ করুন");
      return;
    }
    setSubmitting(true);
    try {
      const room = rooms.find((r) => r.id === form.room_id);
      const payload = { ...form, guests: Number(form.guests), room_name: room ? room.name_en : null };
      await axios.post(`${API}/inquiries`, payload);
      const text = encodeURIComponent(
        `Hello Naljuri Inn,\n\nI'd like to reserve a stay.\n` +
        `Name: ${form.name}\nPhone: ${form.phone}\nArrival: ${form.check_in}\nDeparture: ${form.check_out}` +
        `\nGuests: ${form.guests}\nSuite: ${room ? room.name_en : 'Any'}` +
        (form.message ? `\nNote: ${form.message}` : "")
      );
      window.open(`https://wa.me/${HOTLINE_DIGITS}?text=${text}`, "_blank");
      toast.success(lang === 'en' ? "Reservation request sent — opening WhatsApp" : "রিজার্ভেশন রিকোয়েস্ট পাঠানো হয়েছে — WhatsApp খোলা হচ্ছে");
      setForm({ ...form, name: "", phone: "", message: "" });
    } catch (err) {
      toast.error(lang === 'en' ? "Could not send. Please call us directly." : "পাঠানো যায়নি। আমাদের সরাসরি কল করুন।");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} data-testid="booking-form"
      className={`bg-white border border-forest-100 ${compact ? 'p-6 rounded-md' : 'p-8 md:p-10 rounded-md shadow-[0_24px_60px_-30px_rgba(27,48,34,0.25)]'}`}>
      {!compact && (
        <div className="mb-8">
          <p className="eyebrow tag-line" data-testid="booking-eyebrow">Reservation Form</p>
          <p className="mt-3 text-xs text-slate-warm">Fields marked with * are required. We respond within minutes on WhatsApp.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label={t.booking.name} required>
          <input type="text" required value={form.name} onChange={(e) => update("name", e.target.value)}
                 data-testid="booking-name-input"
                 className="w-full bg-cream-100 border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
        </Field>
        <Field label={t.booking.phone} required>
          <input type="tel" required value={form.phone} onChange={(e) => update("phone", e.target.value)}
                 data-testid="booking-phone-input"
                 className="w-full bg-cream-100 border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
        </Field>
        <Field label={t.booking.checkin}>
          <input type="date" required min={today} value={form.check_in} onChange={(e) => update("check_in", e.target.value)}
                 data-testid="booking-checkin-input"
                 className="w-full bg-cream-100 border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
        </Field>
        <Field label={t.booking.checkout}>
          <input type="date" required min={form.check_in} value={form.check_out} onChange={(e) => update("check_out", e.target.value)}
                 data-testid="booking-checkout-input"
                 className="w-full bg-cream-100 border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
        </Field>
        <Field label={t.booking.guests}>
          <input type="number" min="1" max="20" value={form.guests} onChange={(e) => update("guests", e.target.value)}
                 data-testid="booking-guests-input"
                 className="w-full bg-cream-100 border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
        </Field>
        <Field label={t.booking.room}>
          <select value={form.room_id} onChange={(e) => update("room_id", e.target.value)}
                  data-testid="booking-room-select"
                  className="w-full bg-cream-100 border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600">
            <option value="">{t.booking.select_room}</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {lang === 'en' ? r.name_en : r.name_bn} — ৳{(r.offer_price_bdt || r.price_bdt).toLocaleString()}
              </option>
            ))}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label={t.booking.message}>
            <textarea rows={3} value={form.message} onChange={(e) => update("message", e.target.value)}
                      data-testid="booking-message-input"
                      className="w-full bg-cream-100 border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
          </Field>
        </div>
      </div>

      <button type="submit" disabled={submitting}
              data-testid="booking-submit-btn"
              className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-gold-600 text-cream-100 px-9 py-4 rounded-full text-sm hover:bg-gold-700 transition-all disabled:opacity-60 border border-gold-500/40 shadow-[0_18px_40px_-20px_rgba(184,134,11,0.6)]">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
        {t.booking.submit}
      </button>
    </form>
  );
}

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-widest2 text-gold-600 mb-2">
      {label}{required && <span className="text-gold-700">*</span>}
    </span>
    {children}
  </label>
);
